import { Controller } from "@hotwired/stimulus"
import OlMap from "ol/Map" // NOT `Map` — that would shadow the JS Map used for featuresById
import View from "ol/View"
import Feature from "ol/Feature"
import Point from "ol/geom/Point"
import TileLayer from "ol/layer/Tile"
import VectorLayer from "ol/layer/Vector"
import VectorSource from "ol/source/Vector"
import OSM from "ol/source/OSM"
import WMTS, { optionsFromCapabilities } from "ol/source/WMTS"
import WMTSCapabilities from "ol/format/WMTSCapabilities"
import ScaleLine from "ol/control/ScaleLine"
import { defaults as defaultControls } from "ol/control/defaults"
import { fromLonLat, transformExtent } from "ol/proj"
import CircleStyle from "ol/style/Circle"
import Fill from "ol/style/Fill"
import Stroke from "ol/style/Stroke"
import Style from "ol/style/Style"
import Text from "ol/style/Text"

const EXTENT_WSEN = [16.0, 46.85, 17.3, 48.2] // Burgenland bbox, fills the panel top to bottom
const OPTIONS_KEY = "weather_monitor:ubimet_map:options"
const LABEL_FONT = "bold 12px system-ui, sans-serif"
const BAR_WIDTH = 14
const BAR_HEIGHT = 4
const BAR_FULL_MM = 5

// Client-defined palettes for labels + dots: [lowerBound, background, textColor?]; values floor to a stop and clamp at both ends.
const TEMP_SCALE = [[-20, "#313695", "#fff"], [-15, "#3F66AE", "#fff"], [-10, "#4575B4", "#fff"], [-5, "#5E91C3"], [0, "#74ADD1"], [5, "#9BC8B0"], [10, "#ABDDA4"], [15, "#D5E58F"], [20, "#FEE08B"], [25, "#FDCB73"], [30, "#FDAE61"], [35, "#F88A52"], [40, "#F46D43", "#fff"], [45, "#D8373F", "#fff"], [50, "#A50026", "#fff"]]
const GUST_SCALE = [[0, "#1A9850", "#fff"], [10, "#66BD63"], [20, "#A6D96A"], [30, "#D9EF8B"], [40, "#FEE08B"], [50, "#FDAE61"], [60, "#F46D43", "#fff"], [70, "#D73027", "#fff"], [80, "#A50026", "#fff"]]
// Rain buckets by inclusive upper bound: [max, background, textColor?]; 0 mm renders the bar only, so it has no stop.
const RAIN_SCALE = [[10, "#B3DDF2"], [20, "#74ADD1"], [30, "#4575B4", "#fff"], [40, "#313695", "#fff"], [50, "#00A6A6", "#fff"], [60, "#1A9850", "#fff"], [70, "#FEE08B"], [80, "#F46D43", "#fff"], [Infinity, "#7A0177", "#fff"]]

function scaleIndex(scale, value) {
  for (let i = scale.length - 1; i >= 0; i--) if (value >= scale[i][0]) return i
  return 0
}

function scaleStyles(scale, offsetY) {
  return scale.map(([, background, color]) => [ stationDot(background), new Style({ text: boxedText(background, color, offsetY) }) ])
}

function boxedText(background, color = "#212529", offsetY = 0) {
  return new Text({ font: LABEL_FONT, padding: [2, 4, 2, 4], offsetY, fill: new Fill({ color }), backgroundFill: new Fill({ color: background }), backgroundStroke: new Stroke({ color: "rgba(73, 80, 87, 0.9)", width: 1 }) })
}

function missingStyle() {
  return new Style({ text: new Text({ text: "✗", font: "bold 14px system-ui, sans-serif", offsetY: -10, fill: new Fill({ color: "#d32f2f" }), stroke: new Stroke({ color: "rgba(255, 255, 255, 0.9)", width: 2 }) }) })
}

// declutterMode "none": dots stay visible when their label loses the declutter contest
function stationDot(fill, stroke = "rgba(255, 255, 255, 0.9)") {
  return new Style({ image: new CircleStyle({ radius: 3, declutterMode: "none", fill: fill ? new Fill({ color: fill }) : undefined, stroke: new Stroke({ color: stroke, width: 1.5 }) }) })
}

function rainBarStyle() {
  return new Style({
    renderer: ([x, y], state) => {
      const context = state.context
      const width = BAR_WIDTH * state.pixelRatio
      const height = BAR_HEIGHT * state.pixelRatio
      const left = x - width / 2
      const top = y - height / 2
      const filled = Math.min((state.feature.get("rain") || 0) / BAR_FULL_MM, 1) * width
      context.save()
      context.fillStyle = "rgba(255, 255, 255, 0.8)"
      context.fillRect(left, top, width, height)
      if (filled > 0) {
        context.fillStyle = "#2e9e4f"
        context.fillRect(left, top, filled, height)
      }
      context.strokeStyle = "#1d6b36"
      context.lineWidth = state.pixelRatio
      context.strokeRect(left, top, width, height)
      context.restore()
    }
  })
}

export class UbimetMapController extends Controller {
  static targets = ["panel", "tick", "slider", "timestamp", "liveBadge", "showAll"]
  static values = { periods: Array, observationsUrl: String, initial: Object }

  connect() {
    this.featuresById = new Map() // plain JS Map (OL's is imported as OlMap)
    this.baseMapAbortController = new AbortController()
    this.source = new VectorSource()
    this.baseLayers = []
    this.vectorLayers = []
    this.declutter = this.restoreOptions().declutter !== false
    this.showAllTarget.checked = !this.declutter
    this.view = new View({ center: fromLonLat([16.4, 47.7]), zoom: 9, enableRotation: false })
    this.maps = this.panelTargets.map((panel) => this.buildMap(panel))
    const size = this.maps[0]?.getSize()
    if (size && size[0] > 0 && size[1] > 0) this.view.fit(transformExtent(EXTENT_WSEN, "EPSG:4326", "EPSG:3857"), { size })
    if (this.hasInitialValue && this.initialValue.period) this.applyPayload(this.initialValue)
    this.installResizeObserver()
    void this.upgradeBaseMaps()
    this.mapsReady = true
    this.syncControls()
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    if (this.sizeUpdateFrame) {
      cancelAnimationFrame(this.sizeUpdateFrame)
      this.sizeUpdateFrame = null
    }
    this.baseMapAbortController.abort()
    this.fetchAbortController?.abort()
    this.maps.forEach((map) => map.setTarget(null))
    this.maps = []
  }

  installResizeObserver() {
    if (typeof ResizeObserver === "undefined") return

    this.resizeObserver = new ResizeObserver(() => this.refreshSize())
    this.resizeObserver.observe(this.element)
  }

  refreshSize() {
    if (this.sizeUpdateFrame) cancelAnimationFrame(this.sizeUpdateFrame)

    this.sizeUpdateFrame = requestAnimationFrame(() => {
      this.maps.forEach((map) => map.updateSize())
      this.sizeUpdateFrame = null
    })
  }

  showAllChanged() {
    this.declutter = !this.showAllTarget.checked
    this.vectorLayers.forEach((layer) => layer.setDeclutter(this.declutter))
    this.persistOptions()
  }

  restoreOptions() {
    try { return JSON.parse(localStorage.getItem(OPTIONS_KEY)) || {} } catch { return {} }
  }

  persistOptions() {
    try { localStorage.setItem(OPTIONS_KEY, JSON.stringify({ declutter: this.declutter })) } catch { /* storage unavailable */ }
  }

  buildMap(panel) {
    const layer = new VectorLayer({ source: this.source, declutter: this.declutter, style: this.styleFor(panel.dataset.metric), renderOrder: this.renderOrderFor(panel.dataset.metric) })
    this.vectorLayers.push(layer)
    const base = new TileLayer({ source: new OSM(), zIndex: 0 })
    this.baseLayers.push(base)
    const controls = defaultControls({ rotate: false, attributionOptions: { collapsible: false, collapsed: false } })
    controls.push(new ScaleLine())
    return new OlMap({ target: panel, view: this.view, layers: [base, layer], controls })
  }

  async upgradeBaseMaps() {
    try {
      const response = await fetch("https://mapsneu.wien.gv.at/basemapneu/1.0.0/WMTSCapabilities.xml", { signal: this.baseMapAbortController.signal })
      const capabilities = new WMTSCapabilities().read(await response.text())
      const options = optionsFromCapabilities(capabilities, { layer: "bmapgrau", matrixSet: "google3857", style: "normal" })
      options.attributions = "Data source: basemap.at"
      options.attributionsCollapsible = false
      const source = new WMTS(options)
      this.baseLayers.forEach((layer) => layer.setSource(source))
    } catch (error) {
      if (error.name === "AbortError") return
      console.warn("Failed to load preferred basemap, leaving fallback map in place.", error)
    }
  }

  styleFor(metric) {
    const missing = [ stationDot(null, "#868e96"), missingStyle() ]
    if (metric === "gust") {
      const styles = scaleStyles(GUST_SCALE, -12)
      return (feature) => {
        const value = feature.get("gust")
        if (value == null) return missing
        const style = styles[scaleIndex(GUST_SCALE, value)]
        style[1].getText().setText(`${value} km/h`)
        return style
      }
    }
    if (metric === "rain") {
      const bar = rainBarStyle()
      const labels = RAIN_SCALE.map(([, background, color]) => new Style({ text: boxedText(background, color, -13) }))
      return (feature) => {
        const value = feature.get("rain")
        if (value == null) return missing
        if (value === 0) return bar
        const label = labels[RAIN_SCALE.findIndex(([max]) => value <= max)]
        label.getText().setText(`${value.toLocaleString("de-AT")}mm`)
        return [ bar, label ]
      }
    }
    const styles = scaleStyles(TEMP_SCALE, -12)
    return (feature) => {
      const value = feature.get("temp")
      if (value == null) return missing
      const style = styles[scaleIndex(TEMP_SCALE, value)]
      style[1].getText().setText(`${value.toLocaleString("de-AT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}°C`)
      return style
    }
  }

  renderOrderFor(metric) {
    return (a, b) => (b.get(metric) ?? -Infinity) - (a.get(metric) ?? -Infinity)
  }

  applyPayload(payload) {
    const seen = new Set()
    payload.stations.forEach((station) => {
      seen.add(station.id)
      let feature = this.featuresById.get(station.id)
      if (!feature) {
        feature = new Feature({ geometry: new Point(fromLonLat([station.lon, station.lat])) })
        this.featuresById.set(station.id, feature)
        this.source.addFeature(feature)
      }
      feature.setProperties({ gust: station.gust, rain: station.rain, temp: station.temp, name: station.name })
    })
    for (const [id, feature] of [...this.featuresById]) {
      if (!seen.has(id)) {
        this.source.removeFeature(feature)
        this.featuresById.delete(id)
      }
    }
    this.selectedPeriod = payload.period
    this.timestampTarget.textContent = this.formatPeriod(payload.period)
    this.syncControls()
  }

  formatPeriod(iso) {
    return new Intl.DateTimeFormat("de-AT", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Vienna" }).format(new Date(iso))
  }

  showPeriod(period) {
    if (!period || period === this.selectedPeriod) return
    this.fetchPeriod(period)
  }

  async fetchPeriod(period) {
    this.fetchAbortController?.abort()
    this.fetchAbortController = new AbortController()
    try {
      const response = await fetch(`${this.observationsUrlValue}?period=${encodeURIComponent(period)}`, { signal: this.fetchAbortController.signal, headers: { Accept: "application/json" } })
      if (!response.ok) return
      this.applyPayload(await response.json())
    } catch (error) {
      if (error.name !== "AbortError") throw error
    }
  }

  sliderChanged(event) {
    this.showPeriod(this.periodsValue[Number(event.target.value)])
  }

  stepBack() {
    const index = this.periodsValue.indexOf(this.selectedPeriod)
    this.showPeriod(this.periodsValue[Math.max(index - 1, 0)])
  }

  stepForward() {
    const index = this.periodsValue.indexOf(this.selectedPeriod)
    this.showPeriod(this.periodsValue[Math.min(index + 1, this.periodsValue.length - 1)])
  }

  goLive() {
    this.showPeriod(this.periodsValue.at(-1))
  }

  syncControls() {
    this.live = this.selectedPeriod === this.periodsValue.at(-1)
    const empty = this.periodsValue.length === 0
    this.sliderTarget.max = this.periodsValue.length - 1
    this.sliderTarget.value = this.periodsValue.indexOf(this.selectedPeriod)
    this.sliderTarget.disabled = empty
    this.liveBadgeTarget.classList.toggle("active", this.live)
    this.liveBadgeTarget.disabled = empty
  }

  tickTargetConnected(element) {
    if (!this.mapsReady) return // initial render — connect() already consumed the inline state
    const periods = JSON.parse(element.dataset.periods)
    this.periodsValue = periods
    if (this.live) {
      this.showPeriod(periods.at(-1))
    } else if (!periods.includes(this.selectedPeriod)) {
      this.showPeriod(periods[0]) // selection slid out of the 24h window — clamp to oldest
    } else {
      this.syncControls()
    }
  }
}
